import React, { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import DropdownMultiSelect from "./DropdownMultiSelect";
import { getEntities } from "../API";
import { ReportParamsContext } from "../context/ReportParamsContext";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function filterBanners(all, commonParams) {
  const territories = commonParams.territories ?? [];
  const provinces = commonParams.provinces ?? [];
  if (territories.length > 0) {
    return all.filter((b) =>
      territories.find((t) => b.provinces?.find((bp) => bp.id === t.province?.id))
    );
  }
  if (provinces.length > 0) {
    return all.filter((b) =>
      provinces.find((p) => b.provinces?.find((bp) => bp.id === p.id))
    );
  }
  return all;
}

function ReportPage({ schema }) {
  const [commonParams] = useContext(ReportParamsContext);

  const [values, setValues] = useState(
    Object.fromEntries(
      schema.params.map((p) => [p.name, p.type === "multiselect" ? [] : null])
    )
  );
  // allOptions holds the full unfiltered fetch result; options holds what is shown
  const allOptionsRef = useRef({});
  const [options, setOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [downloading, setDownloading] = useState(false);

  // Load non-cascaded options on mount (size=1000 to avoid pagination cut-off)
  useEffect(() => {
    schema.params
      .filter((p) => p.route && !p.cascadesFrom)
      .forEach((p) => {
        getEntities(p.route, "?size=1000").then((data) => {
          const items = normalizeResponse(data);
          allOptionsRef.current = { ...allOptionsRef.current, [p.name]: items };
          const displayed = p.route === "banners" ? filterBanners(items, commonParams) : items;
          setOptions((prev) => ({ ...prev, [p.name]: displayed }));
        });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-filter banner options whenever province/territory context changes
  useEffect(() => {
    schema.params
      .filter((p) => p.route === "banners" && !p.cascadesFrom)
      .forEach((p) => {
        const all = allOptionsRef.current[p.name];
        if (!all) return;
        // Clear selected banners that are no longer in the filtered list
        const filtered = filterBanners(all, commonParams);
        setValues((prev) => ({
          ...prev,
          [p.name]: p.type === "multiselect"
            ? (prev[p.name] ?? []).filter((sel) => filtered.find((b) => b.id === sel.id))
            : (filtered.find((b) => b.id === prev[p.name]?.id) ? prev[p.name] : null),
        }));
        setOptions((prev) => ({
          ...prev,
          [p.name]: filterBanners(all, commonParams),
        }));
      });
  }, [commonParams.provinces, commonParams.territories]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the shared manufacturer changes, clear and reload cascaded params
  const prevManufacturerId = useRef(undefined);
  useEffect(() => {
    const manufacturerId = commonParams.manufacturer?.id;
    if (manufacturerId === prevManufacturerId.current) return;
    prevManufacturerId.current = manufacturerId;

    setValues((prev) => {
      const next = { ...prev };
      schema.params
        .filter((p) => p.cascadesFrom === "manufacturer" || p.clearOnManufacturerChange)
        .forEach((p) => { next[p.name] = p.type === "multiselect" ? [] : null; });
      return next;
    });
    // Also clear options for params that only clear (don't auto-fetch) on manufacturer change
    schema.params
      .filter((p) => p.clearOnManufacturerChange)
      .forEach((p) => setOptions((prev) => ({ ...prev, [p.name]: [] })));

    schema.params
      .filter((p) => p.cascadesFrom === "manufacturer" && p.route)
      .forEach((p) => {
        if (manufacturerId) {
          setLoadingOptions((prev) => ({ ...prev, [p.name]: true }));
          getEntities(p.route, p.buildQueryFn
            ? p.buildQueryFn(commonParams.manufacturer, commonParams, valuesRef.current)
            : `?search=manufacturer:${manufacturerId}${p.searchExtra ? `&filter=${p.searchExtra}` : ``}&size=1000`)
            .then((data) => setOptions((prev) => ({ ...prev, [p.name]: normalizeResponse(data) })))
            .finally(() => setLoadingOptions((prev) => ({ ...prev, [p.name]: false })));
        } else {
          setOptions((prev) => ({ ...prev, [p.name]: [] }));
        }
      });
  }, [commonParams.manufacturer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch manufacturer-cascaded params with buildQueryFn when provinces/territories change
  const valuesRef = useRef(values);
  valuesRef.current = values;
  useEffect(() => {
    const manufacturer = commonParams.manufacturer;
    if (!manufacturer) return;
    schema.params
      .filter((p) => p.cascadesFrom === "manufacturer" && p.route && p.buildQueryFn)
      .forEach((p) => {
        setLoadingOptions((prev) => ({ ...prev, [p.name]: true }));
        getEntities(p.route, p.buildQueryFn(manufacturer, commonParams, valuesRef.current))
          .then((data) => {
            const newOpts = normalizeResponse(data);
            setOptions((prev) => ({ ...prev, [p.name]: newOpts }));
            setValues((prev) => ({
              ...prev,
              [p.name]: p.type === "multiselect"
                ? (prev[p.name] ?? []).filter((sel) => newOpts.find((o) => o.id === sel.id))
                : (newOpts.find((o) => o.id === prev[p.name]?.id) ? prev[p.name] : null),
            }));
          })
          .finally(() => setLoadingOptions((prev) => ({ ...prev, [p.name]: false })));
      });
  }, [commonParams.provinces, commonParams.territories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch options for non-manufacturer cascaded params with buildQueryFn when provinces/territories change
  useEffect(() => {
    schema.params
      .filter((p) => p.buildQueryFn && p.cascadesFrom && p.cascadesFrom !== "manufacturer" && valuesRef.current[p.cascadesFrom])
      .forEach((p) => {
        const parentValue = valuesRef.current[p.cascadesFrom];
        setLoadingOptions((prev) => ({ ...prev, [p.name]: true }));
        getEntities(p.route, p.buildQueryFn(parentValue, commonParams, valuesRef.current))
          .then((data) => {
            const newOptions = normalizeResponse(data);
            setOptions((prev) => ({ ...prev, [p.name]: newOptions }));
            setValues((prev) => ({
              ...prev,
              [p.name]: p.type === "multiselect"
                ? (prev[p.name] ?? []).filter((sel) => newOptions.find((o) => o.id === sel.id))
                : (newOptions.find((o) => o.id === prev[p.name]?.id) ? prev[p.name] : null),
            }));
          })
          .finally(() => setLoadingOptions((prev) => ({ ...prev, [p.name]: false })));
      });
  }, [commonParams.provinces, commonParams.territories]); // eslint-disable-line react-hooks/exhaustive-deps

  const setValue = (name, value) => {
    const newValues = { ...values, [name]: value };
    schema.params
      .filter((p) => p.cascadesFrom === name)
      .forEach((p) => {
        newValues[p.name] = p.type === "multiselect" ? [] : null;
        if (value && p.route) {
          const query = p.buildQueryFn
            ? p.buildQueryFn(value, commonParams, newValues)
            : `?search=${name}:${value.id}${p.searchExtra ? `&filter=${p.searchExtra}` : ``}&size=1000`;
          setLoadingOptions((prev) => ({ ...prev, [p.name]: true }));
          getEntities(p.route, query)
            .then((data) => setOptions((prev) => ({ ...prev, [p.name]: normalizeResponse(data) })))
            .finally(() => setLoadingOptions((prev) => ({ ...prev, [p.name]: false })));
        } else {
          setOptions((prev) => ({ ...prev, [p.name]: [] }));
        }
      });

    // Re-fetch manufacturer-cascaded params that depend on this local value
    const manufacturer = commonParams.manufacturer;
    if (manufacturer) {
      schema.params
        .filter((p) => p.dependsOnValues?.includes(name) && p.route && p.buildQueryFn)
        .forEach((p) => {
          setLoadingOptions((prev) => ({ ...prev, [p.name]: true }));
          getEntities(p.route, p.buildQueryFn(manufacturer, commonParams, newValues))
            .then((data) => {
              const newOpts = normalizeResponse(data);
              setOptions((prev) => ({ ...prev, [p.name]: newOpts }));
              setValues((prev) => ({
                ...prev,
                [p.name]: p.type === "multiselect"
                  ? (prev[p.name] ?? []).filter((sel) => newOpts.find((o) => o.id === sel.id))
                  : (newOpts.find((o) => o.id === prev[p.name]?.id) ? prev[p.name] : null),
              }));
            })
            .finally(() => setLoadingOptions((prev) => ({ ...prev, [p.name]: false })));
        });
    }

    setValues(newValues);
  };

  const commonReady = !!(
    commonParams.manufacturer &&
    commonParams.fromDate &&
    commonParams.toDate
  );

  const specificReady = schema.params
    .filter((p) => p.required)
    .every((p) => {
      const v = values[p.name];
      if (p.type === "multiselect") return Array.isArray(v) && v.length > 0;
      return v !== null && v !== "" && v !== undefined;
    });

  const handleDownload = async () => {
    const payload = {
      manufacturerId: commonParams.manufacturer?.id,
      fromDate: commonParams.fromDate,
      toDate: commonParams.toDate,
      representativeIds: (commonParams.representatives ?? []).map((r) => r.id),
      territoryIds: (commonParams.territories ?? []).map((t) => t.id),
    };

    schema.params.forEach((p) => {
      const v = values[p.name];
      if (p.type === "select") {
        payload[p.payloadKey ?? p.name + "Id"] = v?.id;
      } else if (p.type === "multiselect") {
        payload[p.payloadKey ?? p.name + "Ids"] = (v ?? []).map((i) => i.id);
      } else {
        payload[p.payloadKey ?? p.name] = v;
      }
    });

    setDownloading(true);
    try {
      await schema.downloadFn(payload);
    } finally {
      setDownloading(false);
    }
  };

  const simpleParams = schema.params.filter((p) => p.type !== "multiselect");
  const multiParams = schema.params.filter((p) => p.type === "multiselect");

  return (
    <div className="flex-grow-1 p-4 overflow-auto">
      <h5 className="mb-4">{schema.title}</h5>

      {simpleParams.length > 0 && (
        <div className="row mb-3">
          {simpleParams.map((p) => (
            <div className="col-md-3" key={p.name}>
              <label className="form-label">{p.label}</label>
              {p.type === "date" && (
                <input
                  type="date"
                  className="form-control"
                  value={values[p.name] ?? ""}
                  onChange={(e) => setValue(p.name, e.target.value)}
                />
              )}
              {p.type === "select" && (
                <Select
                  options={options[p.name] ?? []}
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o.id}
                  value={values[p.name]}
                  onChange={(v) => setValue(p.name, v)}
                  placeholder={`Select ${p.label.toLowerCase()}...`}
                  isClearable
                />
              )}
            </div>
          ))}
        </div>
      )}

      {multiParams.length > 0 && (
        <div className="row g-3 mb-4 align-items-end">
          {multiParams.map((p) => {
            const isLoading = loadingOptions[p.name];
            const parentSatisfied =
              p.cascadesFrom === "manufacturer"
                ? !!commonParams.manufacturer
                : p.cascadesFrom
                ? !!values[p.cascadesFrom]
                : true;
            const labelFn = (o) =>
              (p.fields ?? ["name"]).map((f) => o[f]).filter(Boolean).join(" ");

            return (
              <div className="col-md-3" key={p.name}>
                <label className="form-label small mb-1">
                  {p.label}{isLoading ? " (loading…)" : ""}
                </label>
                <DropdownMultiSelect
                  options={options[p.name] ?? []}
                  value={values[p.name]}
                  onChange={(v) => setValue(p.name, v)}
                  getOptionLabel={labelFn}
                  getOptionValue={(o) => o.id}
                  placeholder={`Select ${p.label.toLowerCase()}...`}
                  disabled={!parentSatisfied || isLoading}
                />
              </div>
            );
          })}
        </div>
      )}

      {!commonReady && (
        <p className="text-muted small mb-3">
          Select a manufacturer and date range in the panel above to enable download.
        </p>
      )}

      <button
        className="btn btn-primary"
        onClick={handleDownload}
        disabled={!commonReady || !specificReady || downloading}
      >
        {downloading ? "Generating..." : "Download Excel"}
      </button>
    </div>
  );
}

export default ReportPage;
